import { Link } from "react-router-dom";
import { simMnpCampaignLinks } from "@/data/simMnpCampaignLinks";
import { approvedAds } from "@/data/approvedAds";
import ahamoPlanImage from "@/assets/editorial/article-illustrations/ahamo-guide/detail-1.webp";
import ahamoSupportImage from "@/assets/editorial/article-illustrations/ahamo-guide/detail-2.webp";
import ahamoUsageImage from "@/assets/editorial/article-illustrations/ahamo-guide/detail-3.webp";
import iijmioCapacityImage from "@/assets/editorial/article-illustrations/iijmio-guide/detail-1.webp";
import iijmioSimImage from "@/assets/editorial/article-illustrations/iijmio-guide/detail-2.webp";
import iijmioSupportImage from "@/assets/editorial/article-illustrations/iijmio-guide/detail-3.webp";
import rakutenPriceImage from "@/assets/editorial/article-illustrations/rakuten-guide/detail-1.webp";
import rakutenCallImage from "@/assets/editorial/article-illustrations/rakuten-guide/detail-2.webp";

type CarrierArticleId = "ahamo-guide" | "iijmio-guide" | "rakuten-guide";

function EditorialFigure({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="not-prose my-8 border-y border-background-300 bg-white py-5">
      <img src={src} alt={alt} className="mx-auto w-full max-w-[560px] object-contain" />
      <figcaption className="mt-4 px-3 text-center text-sm leading-6 text-foreground-600">{caption}</figcaption>
    </figure>
  );
}

function FactTable({ rows }: { rows: { label: string; value: string; note?: string }[] }) {
  return (
    <div className="not-prose my-7 border-y-2 border-foreground-900 bg-white">
      {rows.map((row, index) => (
        <div key={row.label} className={`grid gap-1 px-2 py-4 md:grid-cols-[180px_1fr] md:gap-6 md:px-4 ${index ? "border-t border-background-300" : ""}`}>
          <strong className="text-[15px] text-foreground-900">{row.label}</strong>
          <div className="text-[15px] leading-7 text-foreground-700">
            <span className="font-bold text-foreground-900">{row.value}</span>{row.note && <span className="ml-2 text-sm text-foreground-500">{row.note}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function DecisionList({ items }: { items: { title: string; text: string }[] }) {
  return (
    <div className="not-prose my-7 border-y border-background-400">
      {items.map((item, index) => (
        <div key={item.title} className={`grid gap-2 py-5 md:grid-cols-[190px_1fr] md:gap-7 ${index ? "border-t border-background-300" : ""}`}>
          <strong className="text-[16px] leading-7 text-foreground-900">{item.title}</strong>
          <div className="text-[15px] leading-7 text-foreground-700">{item.text}</div>
        </div>
      ))}
    </div>
  );
}

function SourceLine({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noreferrer" className="font-semibold">{children} ↗</a>;
}

function NextActions() {
  return (
    <section id="next-actions">
      <h2>迷ったら、料金より先に使い方を整理</h2>
      <p>同じプランでも、データ量・電話・サポートの希望で向き不向きが変わります。候補を絞ってから、今の通信費と同じ範囲で比べてください。</p>
      <div>
        <Link to="/diagnosis"><small>使い方から候補を絞る</small><strong>3つの質問で診断する</strong><span>→</span></Link>
        <Link to="/compare"><small>条件を横に並べる</small><strong>通信会社を比較する</strong><span>→</span></Link>
      </div>
    </section>
  );
}

function AhamoArticle() {
  const ahamoAdMarkup = simMnpCampaignLinks.ahamo.adMarkup;
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">「スマホ代はこれくらい仕方ない」と、毎月7,000円を払い続けていませんか。ahamoの月額2,970円へ変えられれば、差額は月4,030円。1年間では48,360円です。</p>
        <p>しかも、通常30GBのデータ容量と1回5分以内の国内通話込み。長電話が多くても、完全かけ放題を付けて月額4,070円です。ドコモ回線を使いながら、通信費を下げたい人には有力な選択肢です。</p>
        <p>さらに現在は、今のスマホのまま他社から乗り換えるとdポイント20,000pt、スマホも購入する場合は対象端末が最大44,000円割引になるキャンペーンが案内されています。</p>
      </section>

      <section id="answer">
        <h2>この3つに当てはまるなら、ahamoを申し込む価値がある</h2>
        <ol>
          <li>端末代を除く通信費を、毎月5,000円以上払っている。</li>
          <li>データ使用量は月10〜30GB程度。現在の増量中なら40GB以内。</li>
          <li>申込みと初期設定をオンラインで進められる。</li>
        </ol>
        <p>3つとも当てはまるなら、「安くしたいけれど、回線品質や端末購入で失敗したくない」という不安を抱えたまま、今の料金を払い続ける必要はありません。今のスマホを使うか、買い替えるかで申込み方法だけ選びましょう。</p>
      </section>

      <section id="quick-decision">
        <h2>あなたは、どちらから申し込む？</h2>
        <div className="not-prose my-7 grid gap-4 md:grid-cols-2">
          <div className="border-y-2 border-foreground-900 bg-white px-5 py-6">
            <span className="text-sm font-semibold text-foreground-500">今のスマホを使う人</span>
            <strong className="mt-2 block text-2xl text-primary-700">SIMのみで20,000pt</strong>
            <p className="mt-3 text-sm leading-7 text-foreground-700">他社からMNPし、端末を購入しない人向け。先にキャンペーンへエントリーしてからahamoを申し込みます。</p>
            <p className="mt-4 text-xs font-bold text-primary-700">PR｜SIMのみ乗り換えの申込先</p>
            {ahamoAdMarkup && <div className="mt-2 border-2 border-foreground-900 bg-[#FFE16A] px-4 py-3 text-center font-bold text-foreground-900 transition hover:bg-[#FFD43B] [&_a]:block [&_a]:text-foreground-900" dangerouslySetInnerHTML={{ __html: ahamoAdMarkup }} />}
            <p className="mt-2 text-xs leading-6 text-foreground-600">公式キャンペーンページで条件を確認し、先にエントリーしてください。</p>
          </div>
          <div className="border-y-2 border-foreground-900 bg-white px-5 py-6">
            <span className="text-sm font-semibold text-foreground-500">スマホも買い替える人</span>
            <strong className="mt-2 block text-2xl text-primary-700">対象端末が最大44,000円割引</strong>
            <p className="mt-3 text-sm leading-7 text-foreground-700">他社からMNPし、対象のiPhone・Androidを一緒に購入する人向け。機種ごとの直接割引を確認します。</p>
            <a href="https://ahamo.com/special/lp/5g-welcome_wari.html" target="_blank" rel="noreferrer" className="mt-4 block bg-primary-600 px-4 py-3 text-center font-bold text-white">割引対象スマホを見る ↗</a>
          </div>
        </div>
        <p className="text-sm text-foreground-500">ドコモからahamoへのプラン変更は他社からのMNPではないため、上記の乗り換え特典とは条件が異なります。</p>
      </section>

      <section id="savings">
        <h2>今の通信費が高いほど、毎月の差が積み上がる</h2>
        <div className="not-prose my-7 overflow-x-auto border-y-2 border-foreground-900 bg-white">
          <table className="w-full min-w-[680px] border-collapse text-left text-sm">
            <thead className="bg-background-100 text-foreground-900"><tr><th className="p-3">現在の通信費</th><th className="p-3">ahamoとの差額／月</th><th className="p-3">1年間の差額</th></tr></thead>
            <tbody>
              <tr className="border-t border-background-300"><td className="p-3">5,000円</td><td className="p-3 font-bold">2,030円</td><td className="p-3 font-bold text-primary-700">24,360円</td></tr>
              <tr className="border-t border-background-300"><td className="p-3">6,000円</td><td className="p-3 font-bold">3,030円</td><td className="p-3 font-bold text-primary-700">36,360円</td></tr>
              <tr className="border-t border-background-300"><td className="p-3">7,000円</td><td className="p-3 font-bold">4,030円</td><td className="p-3 font-bold text-primary-700">48,360円</td></tr>
              <tr className="border-t border-background-300"><td className="p-3">8,000円</td><td className="p-3 font-bold">5,030円</td><td className="p-3 font-bold text-primary-700">60,360円</td></tr>
            </tbody>
          </table>
        </div>
        <p>これは現在の通信費とahamoの基本料金2,970円を比べた単純計算です。端末代、オプション、家族・光回線の割引変動は別に確認します。</p>
        <aside><strong>年間48,360円を、通信会社へ払い続けますか。</strong><p>家族との外食、旅行、日々の生活費。毎月は4,030円でも、1年分を合計すると使い道を選び直せる金額になります。</p></aside>
      </section>

      <section id="benefits">
        <h2>ahamoを選ぶ8つのメリット</h2>
        <DecisionList items={[
          { title: "1．割引条件に悩まない", text: "家族回線数、光回線、指定カードをそろえなくても月額2,970円。「結局、自分はいくらなの？」と複雑な割引を追いかけずに済みます。" },
          { title: "2．月末のギガ不安を減らす", text: "通常は月30GB。増量キャンペーン中は40GBです。外出先で動画、SNS、地図を使うたび、残り容量を気にする場面を減らせます。" },
          { title: "3．電話時間を気にしない", text: "短い電話は1回5分まで追加料金なし。長電話が多い人もかけ放題を付けて合計4,070円。時計を見ながら慌てて電話を切る必要がありません。" },
          { title: "4．端末探しで迷わない", text: "乗り換えと対象スマホの購入をまとめて進められます。「この端末で使えるのか」を一から調べて別々に契約する手間を減らせます。" },
          { title: "5．買い替え負担も下げる", text: "対象機種には乗り換え割引があります。今のスマホを使う人は20,000pt、買い替える人は端末割引という、自分に合う特典を選べます。" },
          { title: "6．回線の不安を残さない", text: "料金はオンラインプランでも、利用するのはドコモの4G・5G回線です。安さのために、知らない回線へ変える不安を減らせます。" },
          { title: "7．海外準備をひとつ減らす", text: "対象国・地域なら追加申込みなしでデータ通信を利用できます。海外用Wi-Fiの受取りや現地SIM探しを減らし、到着後すぐ連絡や地図を使えます。" },
          { title: "8．始める費用を抑える", text: "ahamoサイトでの新規契約・乗り換えは事務手数料0円。機種変更もドコモオンラインショップなら無料です。" },
        ]} />
      </section>

      <section id="price">
        <h2>月額2,970円で、何が含まれる？</h2>
        <FactTable rows={[
          { label: "ahamo", value: "月額2,970円", note: "通常30GB／増量中40GB" },
          { label: "ahamo大盛り", value: "月額4,950円", note: "通常110GB／増量中120GB" },
          { label: "国内通話", value: "1回5分以内は追加料金なし", note: "超過後22円／30秒" },
          { label: "かけ放題", value: "月額1,100円", note: "ahamoと合計4,070円" },
          { label: "データ追加", value: "1GBにつき550円", note: "容量超過時" },
          { label: "事務手数料", value: "0円", note: "機種代金などは別" },
        ]} />
        <p>たとえば1分程度の電話を月20回かけると、22円／30秒の従量通話では合計880円です。ahamoなら、すべて1回5分以内であれば追加料金はかかりません。</p>
        <EditorialFigure src={ahamoPlanImage} alt="ahamoのデータ容量と短時間通話を確認するイラスト" caption="料金にはデータ容量と短時間通話が含まれます。通常時の容量で考えます。" />
      </section>

      <section id="unlimited-call">
        <h2>長電話が多くても、月額4,070円で完全かけ放題</h2>
        <p>5分を超える電話が多い人には、月額1,100円の「かけ放題オプション」があります。ahamoの基本料金と合わせても月額4,070円です。</p>
        <div className="not-prose my-7 overflow-x-auto border-y-2 border-foreground-900 bg-white">
          <table className="w-full min-w-[680px] border-collapse text-left text-sm">
            <thead className="bg-background-100 text-foreground-900"><tr><th className="p-3">サービス</th><th className="p-3">完全かけ放題の追加料金</th><th className="p-3">ahamoとの差</th></tr></thead>
            <tbody>
              <tr className="border-t border-background-300"><td className="p-3 font-bold">ahamo</td><td className="p-3 text-lg font-bold text-primary-700">1,100円／月</td><td className="p-3 font-bold">基準</td></tr>
              <tr className="border-t border-background-300"><td className="p-3 font-bold">ドコモ</td><td className="p-3">1,980円／月</td><td className="p-3">月880円高い</td></tr>
              <tr className="border-t border-background-300"><td className="p-3 font-bold">au</td><td className="p-3">1,980円／月</td><td className="p-3">月880円高い</td></tr>
              <tr className="border-t border-background-300"><td className="p-3 font-bold">SoftBank</td><td className="p-3">1,980円／月</td><td className="p-3">月880円高い</td></tr>
              <tr className="border-t border-background-300"><td className="p-3 font-bold">UQ mobile</td><td className="p-3">1,980円／月</td><td className="p-3">月880円高い</td></tr>
              <tr className="border-t border-background-300"><td className="p-3 font-bold">Y!mobile</td><td className="p-3">1,980円／月</td><td className="p-3">S・Mの場合。月880円高い</td></tr>
            </tbody>
          </table>
        </div>
        <p>大手3社や主要サブブランドの月額1,980円と比べると、ahamoは毎月880円、年間では10,560円安くなります。仕事、家族、病院などへ長く電話する人は、「長電話だからahamoに向かない」のではなく、最初から月額4,070円で比較すればよいのです。</p>
        <p>なお、オンライン専用プランには、LINEMOベストプランVのように追加1,100円のサービスや、povo2.0の1,650円などもあります。「他社はすべて1,980円」ではありませんが、大手・サブブランドと比べたahamoの通話料金は明確なメリットです。</p>
        <aside><strong>月途中でも追加できます。</strong><p>ahamoサイト・アプリから申し込め、申込み後の通話から適用されます。月途中でも1,100円は日割りになりません。0570など一部の電話番号はかけ放題の対象外です。</p></aside>
      </section>

      <section id="device">
        <h2>オンライン専用でも、スマホ購入までまとめてできる</h2>
        <p>格安SIMやオンライン専用プランの中には、回線だけを契約し、端末を別の店で用意するサービスもあります。ahamoなら、他社から乗り換える際に、対象端末と回線を一緒に申し込めます。</p>
        <div className="not-prose my-7 overflow-x-auto border-y-2 border-foreground-900 bg-white">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead className="bg-background-100 text-foreground-900"><tr><th className="p-3">希望</th><th className="p-3">選ぶ手続き</th><th className="p-3">確認すること</th></tr></thead>
            <tbody>
              <tr className="border-t border-background-300"><td className="p-3">今のスマホを使う</td><td className="p-3 font-bold">SIM・eSIMのみ申し込む</td><td className="p-3">ahamo対応端末か確認</td></tr>
              <tr className="border-t border-background-300"><td className="p-3">乗り換えと同時に買う</td><td className="p-3 font-bold">ahamoサイトで対象端末を購入</td><td className="p-3">一括・分割、端末割引、返却条件</td></tr>
              <tr className="border-t border-background-300"><td className="p-3">契約後に機種変更する</td><td className="p-3 font-bold">ドコモオンラインショップ</td><td className="p-3">オンラインなら事務手数料無料</td></tr>
            </tbody>
          </table>
        </div>
        <p>端末を別の店で探し、対応機種を調べ、回線だけ後から契約する手間を減らせます。ただし、ahamoサイトで扱う端末はドコモオンラインショップ商品の一部です。</p>
      </section>

      <section id="campaign">
        <h2>2026年10月5日に確認したキャンペーン</h2>
        <p>今のスマホを使う人と、新しいスマホを買う人では対象になる特典が違います。先に申込み方法を決めてから、条件を確認しましょう。</p>
        <div className="not-prose my-7 overflow-x-auto border-y-2 border-foreground-900 bg-white">
          <table className="w-full min-w-[820px] border-collapse text-left text-sm">
            <thead className="bg-background-100 text-foreground-900"><tr><th className="p-3">キャンペーン</th><th className="p-3">特典</th><th className="p-3">主な対象・条件</th><th className="p-3">期間</th></tr></thead>
            <tbody>
              <tr className="border-t border-background-300 align-top">
                <td className="p-3 font-bold">SIMのみ乗り換え</td>
                <td className="p-3 text-lg font-bold text-primary-700">dポイント20,000pt</td>
                <td className="p-3 leading-6">他社からMNP、端末を購入せずSIM・eSIMのみ契約、事前エントリー、同じdアカウントで申込み・開通。新規番号や端末同時購入は対象外。</td>
                <td className="p-3">終了日未定</td>
              </tr>
              <tr className="border-t border-background-300 align-top">
                <td className="p-3 font-bold">対象端末と乗り換え</td>
                <td className="p-3 text-lg font-bold text-primary-700">最大44,000円割引</td>
                <td className="p-3 leading-6">他社からMNPし、ahamoサイトなどで対象の5Gスマホを購入。割引額は機種・容量・購入方法で異なる。</td>
                <td className="p-3">機種ごとの現行割引・期間を公式ページで確認</td>
              </tr>
              <tr className="border-t border-background-300 align-top">
                <td className="p-3 font-bold">データ増量おためし</td>
                <td className="p-3 text-lg font-bold text-primary-700">30GB→40GB</td>
                <td className="p-3 leading-6">ahamo契約者へ申込み不要で自動適用。大盛りは110GBから120GBへ増量。</td>
                <td className="p-3">2026年11月30日まで</td>
              </tr>
            </tbody>
          </table>
        </div>
        <aside><strong>SIMのみ20,000ptと端末割引は、同時にもらう前提で計算しない。</strong><p>20,000ptの対象は端末を購入しないMNPです。スマホも買う場合は、対象端末の直接割引を確認します。キャンペーン同士を重複できない場合もあります。</p></aside>
        <h3>今のスマホを使うなら、20,000ptの申込み順が重要</h3>
        <ol>
          <li>キャンペーンページから、申込みに使うdアカウントでエントリーする。</li>
          <li>ahamoサイトで「他社からの乗り換え」「端末購入なし」を選ぶ。</li>
          <li>エントリー時と同じdアカウントで申込み、期限内に開通する。</li>
          <li>条件達成月の翌々月以降、毎月4,000ptを5か月受け取る。</li>
        </ol>
        <p>エントリーを忘れる、端末を同時購入する、新しい電話番号で契約する、別のdアカウントを使うと20,000ptの対象外になる可能性があります。</p>
        <h3>スマホも買うなら、返却するかどうかを決める</h3>
        <p>対象端末には乗り換え（MNP）による値引きが案内されています。約2年で返却する人は、返却までに払う端末代、返却時の利用料、毎月の通信費を分けて確認してください。返却せず使い続ける人は、値引き後の端末代を最後まで払う場合の総額を確認します。</p>
        <div className="not-prose my-7 border-y-2 border-foreground-900 bg-white px-4 py-5">
          <div className="grid gap-3 text-sm leading-7 text-foreground-700 md:grid-cols-[190px_1fr]">
            <strong className="text-foreground-900">端末価格</strong><span>端末を買い切る場合の販売価格</span>
            <strong className="text-foreground-900">直接割引</strong><span>購入価格からその場で引かれる金額</span>
            <strong className="text-foreground-900">ポイント還元</strong><span>条件達成後に受け取るポイント。値引きとは分けて考える</span>
            <strong className="text-foreground-900">返却時の負担額</strong><span>所定の時期に返却した場合の端末代。返却時の利用料と通信費は別に確認</span>
          </div>
        </div>
        <p>キャンペーンは申込日、契約方法、年齢、対象機種などで変わります。「最大○円」だけでなく、自分の申込み方法が対象か確認してください。</p>
        <div className="not-prose my-8 grid gap-3 md:grid-cols-2">
          <a href="#quick-decision" className="border-2 border-primary-600 bg-primary-600 px-5 py-4 text-center font-bold text-white transition hover:bg-primary-700">SIMのみ20,000ptの申込先へ ↑</a>
          <a href="https://ahamo.com/special/lp/5g-welcome_wari.html" target="_blank" rel="noreferrer" className="border-2 border-foreground-900 bg-white px-5 py-4 text-center font-bold text-foreground-900 transition hover:bg-background-100">最大44,000円の対象端末を見る ↗</a>
        </div>
        <p className="text-sm text-foreground-500">キャンペーンは2026年10月5日再確認。SIMのみの申込先は上部のPRリンクからahamo公式キャンペーンページへ進みます。端末割引のリンクは公式サイトへの直接リンクです。キャンペーン内容・対象機種・在庫・終了日は変更される場合があります。</p>
      </section>

      <section id="concrete-cases">
        <h2>3人の使い方で、向き・不向きを比べる</h2>
        <DecisionList items={[
          { title: "月25GB・短い電話", text: "月額2,970円で収まりやすい使い方です。30GBと5分通話を無駄なく利用でき、ahamoのメリットを感じやすい人です。" },
          { title: "月5GB・電話なし", text: "毎月25GB以上を余らせます。小容量プランの方が安くなる可能性があるため、ahamoだけで決めず比較が必要です。" },
          { title: "月35GB・長電話あり", text: "現在の増量中なら、かけ放題を付けても月額4,070円です。通常30GBへ戻った後は、データを5GB追加すると合計6,820円になるため、大盛り＋かけ放題の月額6,050円を選ぶ方が安くなります。" },
        ]} />
      </section>

      <section id="cautions">
        <h2>気になる点は、申込み前にこう解決する</h2>
        <ul>
          <li><strong>40GBを使い切る人：</strong>増量は11月30日まで。12月1日からサービス仕様・料金の改定が予告されているため、今後の条件も公式ページで確認する。足りなければ大盛りを選ぶ。</li>
          <li><strong>長電話が多い人：</strong>月額1,100円のかけ放題を付け、合計4,070円で利用する。</li>
          <li><strong>設定に不安がある人：</strong>無料チャットを使う。店頭支援が必要なら1回3,300円を見込む。</li>
          <li><strong>家族がドコモの人：</strong>ahamo自体は割引されないが、みんなドコモ割の回線数にはカウントされる。</li>
          <li><strong>海外へ長期滞在する人：</strong>15日以内の旅行なら候補。16日目以降も高速通信が必要なら別の海外通信手段を準備する。</li>
        </ul>
        <EditorialFigure src={ahamoSupportImage} alt="オンラインでスマートフォンの手続きをするイラスト" caption="基本はオンライン手続き。店頭支援が必要なら費用も含めて判断します。" />
      </section>

      <section id="before-change">
        <h2>申込み前の最終チェック</h2>
        <ol>
          <li>直近3か月のデータ使用量は、通常容量の30GBで足りるか。</li>
          <li>5分を超える電話は、月に何回・何分あるか。</li>
          <li>今の端末を使うか、乗り換えと同時に購入するか。</li>
          <li>端末割引、ポイント還元、返却プログラムを分けて比較したか。</li>
          <li>初期設定と開通手続きを自分で進められるか。</li>
        </ol>
        <EditorialFigure src={ahamoUsageImage} alt="スマートフォンのデータ使用量を比べるイラスト" caption="通常30GBで足りるか確認し、SIMのみ・端末セットのどちらで申し込むか決めます。" />
      </section>

      <section id="apply">
        <h2>ahamoを申し込むなら、先に端末の有無を決める</h2>
        <p>今のスマホが対応しているならSIM・eSIMのみ、新しいスマホが必要なら対象端末と一緒に申し込みます。申込途中で迷わないよう、本人確認書類、支払い方法、dアカウントを準備してから進めてください。</p>
        <div className="not-prose my-8 grid gap-3 md:grid-cols-2">
          <div className="border-2 border-foreground-900 bg-[#FFF8D6] px-5 py-4 text-center">
            <p className="mb-2 text-xs font-bold text-foreground-900">PR｜今のスマホのままSIMのみMNP</p>
            {ahamoAdMarkup && <div className="border-2 border-foreground-900 bg-[#FFE16A] px-4 py-3 font-bold text-foreground-900 transition hover:bg-[#FFD43B] [&_a]:block [&_a]:text-foreground-900" dangerouslySetInnerHTML={{ __html: ahamoAdMarkup }} />}
          </div>
          <a href="https://ahamo.com/products/" target="_blank" rel="noreferrer" className="border-2 border-primary-600 bg-white px-5 py-4 text-center font-bold text-primary-700 transition hover:bg-background-100">新しいスマホと一緒に申し込む ↗</a>
        </div>
        <p className="text-sm text-foreground-500">SIMのみのPRリンクから進む前に、公式キャンペーンページでエントリー時期と適用条件を確認してください。端末購入の申込先はahamo公式サイトへの直接リンクです。</p>
      </section>

      <section id="sources">
        <h2>参照した公式情報</h2>
        <ul>
          <li><SourceLine href="https://ahamo.com/plan/index.html">ahamo「料金・データ量」</SourceLine></li>
          <li><SourceLine href="https://faq.ahamo.com/faq/show/3?category_id=10&amp;site_domain=default">国内通話とかけ放題</SourceLine></li>
          <li><SourceLine href="https://www.docomo.ne.jp/charge/onsei_option/">ドコモの音声オプション</SourceLine></li>
          <li><SourceLine href="https://www.au.com/mobile/charge/smartphone/plan/calling-option2/">auの通話定額</SourceLine></li>
          <li><SourceLine href="https://www.ymobile.jp/plan/tuwaoptionplus/">Y!mobileの通話オプション</SourceLine></li>
          <li><SourceLine href="https://faq.ahamo.com/faq/show/900?category_id=15&amp;site_domain=default">新規・乗り換えと端末購入</SourceLine></li>
          <li><SourceLine href="https://faq.ahamo.com/faq/show/118?category_id=14&amp;site_domain=default">機種変更の手続き</SourceLine></li>
          <li><SourceLine href="https://ahamo.com/special/lp/5g-welcome_wari.html">5G WELCOME割</SourceLine></li>
          <li><SourceLine href="https://ahamo.com/special/lp/dpointcp/001.html">SIMのみ乗り換え20,000pt</SourceLine></li>
          <li><SourceLine href="https://faq.ahamo.com/faq/show/4124?site_domain=default">データ増量おためしキャンペーン</SourceLine></li>
          <li><SourceLine href="https://faq.ahamo.com/faq/show/310?site_domain=default">店頭サポートの料金</SourceLine></li>
          <li><SourceLine href="https://ahamo.com/services/roaming-data/index.html">海外データ通信</SourceLine></li>
        </ul>
        <p>キャンペーン確認日：2026年10月5日。増量は11月30日まで。公式FAQでは12月1日からサービス仕様・料金の改定が予告されています。12月以降の条件は公式案内で確認してください。</p>
      </section>
      <NextActions />
    </>
  );
}

function IijmioArticle() {
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">IIJmioは「とにかく安い会社」というより、使うデータ量に合わせて細かく選びたい人向けです。2GBから55GBまで段階が多いぶん、SIMの種類や回線タイプで迷いやすい点もあります。</p>
        <p>料金だけを見て申し込む前に、昼休みの通信、サポート方法、SIMの種類まで確認しておきましょう。</p>
      </section>

      <section id="answer">
        <h2>先に結論：毎月の使用量が読める人ほど選びやすい</h2>
        <p>自宅や職場のWi-Fiを使い、月によってデータ量が大きく変わらない人には合わせやすい料金設計です。一方、混雑する時間帯にも動画や決済を快適に使いたい方は、価格だけで決めず通信の優先度を考えます。</p>
      </section>

      <section id="price">
        <h2>音声SIMの料金は、容量ごとにこう変わる</h2>
        <FactTable rows={[
          { label: "2GB", value: "月額850円", note: "Wi-Fi中心" },
          { label: "5GB", value: "月額950円", note: "外では軽い利用" },
          { label: "10GB", value: "月額1,400円", note: "SNS・地図を日常利用" },
          { label: "15GB", value: "月額1,600円", note: "動画もときどき見る" },
          { label: "25GB", value: "月額2,000円", note: "外での利用が多め" },
          { label: "35・45・55GB", value: "2,400円・3,300円・3,900円", note: "大容量帯" },
        ]} />
        <p>これは音声通話SIM・音声eSIMの通常月額です。データ専用SIMは料金が異なります。キャンペーン価格ではなく、終了後も払う通常料金で比べます。</p>
        <EditorialFigure src={iijmioCapacityImage} alt="IIJmioのデータ容量を選ぶイラスト" caption="直近の使用量に近い容量を選べることが、IIJmioの分かりやすい利点です。" />
      </section>

      <section id="fit">
        <h2>安さの代わりに、自分で決める項目が増える</h2>
        <DecisionList items={[
          { title: "回線タイプ", text: "ドコモ網・au網など、SIMの種類によって選択肢が変わります。今の端末が対応しているか、動作確認端末一覧で機種名まで確認します。" },
          { title: "通信の混雑", text: "MVNOは時間帯によって体感速度が変わることがあります。昼休みに動画や画像の多いSNSをよく使うなら、料金差と使い勝手を天秤にかけます。" },
          { title: "サポート", text: "契約後の確認や変更はオンライン・電話・チャットが中心です。量販店などの受付カウンターが、初期設定や継続サポートまで行うとは限りません。" },
          { title: "容量の見直し", text: "同一mioID内のデータシェアなど選択肢があります。まず1回線の実使用量を見てから、家族分をまとめるか考える方が整理しやすくなります。" },
        ]} />
        <EditorialFigure src={iijmioSimImage} alt="SIMカードとeSIMを選ぶイラスト" caption="SIMカードかeSIMか、端末と回線タイプが合うかを申し込み前に確認します。" />
      </section>

      <section id="before-change">
        <h2>申し込み画面を開く前の確認メモ</h2>
        <ul>
          <li>端末の正確な機種名とSIMロック・対応回線</li>
          <li>音声通話SIM、音声eSIM、データSIMのどれが必要か</li>
          <li>直近3か月で最も多く使ったデータ量</li>
          <li>通話定額を付けた場合の月額合計</li>
          <li>店頭サポートがなくても困らないか</li>
        </ul>
        <EditorialFigure src={iijmioSupportImage} alt="昼休みの通信とオンラインサポートを確認するイラスト" caption="月額だけでなく、混雑する時間帯の使い方とサポート方法も比べます。" />
        <p>料金は<SourceLine href="https://help.iijmio.jp/answer/602dc1d407f19b0012186e68/">IIJmio公式Q&amp;A「ギガプランの月額料金」</SourceLine>で確認できます。確認日：2026年9月18日。</p>
      </section>
      <NextActions />
    </>
  );
}

function RakutenArticle() {
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">月末だから動画を我慢する。親との電話が長くなり、通話料が気になって話を切り上げる。iPhoneを買い替えたいのに、10万円を超える価格を見て諦める——楽天モバイルは、この3つの我慢をまとめて減らせる通信会社です。</p>
        <p>データは月額1,078円から3,278円で、20GBを超えると無制限。Rakuten Linkからかければ国内通話も無料です。さらに、回線の乗り換えとiPhone・Androidの購入をまとめられ、対象機種には端末特典があります。</p>
      </section>

      <section id="answer">
        <h2>使わなかった月は安く、使った月も心配しなくていい</h2>
        <p>Wi-Fi中心だった月に、大容量プランの料金を払い続ける必要はありません。反対に、旅行や入院、テザリングで急にデータを使った月も、国内料金は3,278円が上限です。使う量が毎月変わる人ほど、「今月は何GBに変更しよう」と考える手間から解放されます。</p>
      </section>

      <section id="price">
        <h2>月1,078〜3,278円。たくさん使った月は無制限</h2>
        <FactTable rows={[
          { label: "3GBまで", value: "月額1,078円", note: "家族割適用時968円" },
          { label: "3GB超〜20GB", value: "月額2,178円", note: "家族割適用時2,068円" },
          { label: "20GB超", value: "月額3,278円", note: "家族割適用時3,168円・データ無制限" },
        ]} />
        <p>契約時に容量を決める必要はありません。あまり使わなかった月は自動的に1,078円、旅行やテザリングで20GBを超えた月でも3,278円です。家族4人で最強家族割に参加すれば、合計で月440円、年間5,280円下がります。</p>
        <p>無制限でも、混雑時など公平なサービス提供のため速度制御される場合があります。</p>
        <EditorialFigure src={rakutenPriceImage} alt="楽天モバイルの段階制料金を確認するイラスト" caption="使ったデータ量に応じて、その月の料金帯が決まります。" />
      </section>

      <section id="link-call">
        <h2>長電話する人ほど、Rakuten Linkの価値が大きい</h2>
        <p>「そろそろ切らないと通話料が高くなる」と時計を見る必要がありません。Rakuten Linkアプリから発信すれば、携帯電話や固定電話への国内通話が何分でも無料です。離れて暮らす親や子どもとも、料金を理由に話を切り上げずに済みます。</p>
        <FactTable rows={[
          { label: "Rakuten Link", value: "国内通話無料", note: "時間・回数の制限なし" },
          { label: "標準電話アプリ", value: "22円／30秒", note: "30分で1,320円" },
          { label: "無料対象外", value: "0570・188など", note: "発信前に画面で確認可能" },
        ]} />
        <aside><strong>無料通話はRakuten Linkから発信することが条件です。</strong><p>標準電話アプリから発信した場合と、0570など一部の番号は有料です。</p></aside>
        <EditorialFigure src={rakutenCallImage} alt="通話アプリから電話をかけるイラスト" caption="無料通話は、対象の通話アプリから発信することが条件です。" />
      </section>

      <section id="benefits">
        <h2>楽天モバイルを選ぶ8つのメリット</h2>
        <DecisionList items={[
          { title: "1．使わない月は安い", text: "Wi-Fi中心だった月は3GBまで1,078円。使わなかったギガへ毎月払い続ける無駄を減らせます。" },
          { title: "2．使った月も安心", text: "20GBを超えると月3,278円で無制限。旅行中に地図や動画を使っても、月末の残量を気にしなくて済みます。" },
          { title: "3．長電話を切らなくていい", text: "Rakuten Linkなら国内通話無料。家族との話が長くなっても、時計を見て慌てる必要がありません。" },
          { title: "4．家族全員に効く", text: "最強家族割は1人あたり月110円引き。4人なら年間5,280円、通信費以外に回せるお金が増えます。" },
          { title: "5．端末探しで迷わない", text: "iPhoneとAndroidを販売。使える端末を別の店で探さず、乗り換えと買い替えをまとめて進められます。" },
          { title: "6．iPhoneを諦めなくていい", text: "対象機種には値引きやポイント還元があります。10万円を超える価格だけを見て諦める前に、乗り換え後の負担を確認できます。" },
          { title: "7．対面でも相談できる", text: "Web申込みが不安なら楽天モバイルショップで相談できます。家族の乗り換えや端末購入も、画面だけで悩まずに済みます。" },
          { title: "8．契約だけでもポイント", text: "SIMのみのMNPでも特典があります。通常の乗り換え特典と、専用ページから申し込む三木谷キャンペーンは条件が異なります。" },
        ]} />
      </section>

      <section id="campaign">
        <h2>SIMのみMNPは申込経路で特典が変わる</h2>
        <p>2026年10月5日確認の乗り換え特典は条件を満たすと10,000ポイント。別に、<strong>「楽天・三木谷浩史の特別なお客様キャンペーン」はMNPで14,000ポイント</strong>です。2つを合算して24,000ポイントとは計算しません。</p>
        <p>三木谷キャンペーンをWebで利用するには、専用ページのボタンから申し込み、指定のクーポンコードが適用されていることを申込内容で確認します。翌月末までの開通とRakuten Linkから10秒以上の通話も必要です。ポイントは3回に分けて進呈されます。<a href="https://network.mobile.rakuten.co.jp/campaign/rakuten-employee/entry/vip/" target="_blank" rel="noreferrer">三木谷キャンペーンの公式条件を確認する ↗</a></p>
        <p>通常の申込ページから進むと、三木谷キャンペーンが適用されない場合があります。14,000ポイントを利用したい人は、上の専用ページから申込みを始め、クーポンコードを確認してください。</p>
      </section>

      <section id="device">
        <h2>最新のiPhone 18 Proも、楽天モバイルで買える</h2>
        <p>「本当はiPhoneを替えたい。でも、回線を安くしても端末代が高い」と止まっていた人にとって、楽天モバイルは回線と端末を同時に見直せる選択肢です。</p>
        <p>iPhone 18 Pro 256GBの本体価格は255,900円です。楽天モバイルは、3GBまでの通信費と48回払いの端末代を合わせて月6,299円からと案内しています。通信費と端末代は別々の支払いです。返却プログラムの条件を満たして25か月目以降に端末を返すと、残りの分割代金が不要になります。返却時の事務手数料や故障時の費用は別に確認してください。</p>
        <p>現在は、iPhone 18 Pro／18 Pro Maxの購入と対象プランの申込みで<strong>20,000ポイント</strong>。さらに楽天モバイルを紹介すると、1人4,000ポイント、5人で最大20,000ポイントが加わり、合計最大40,000ポイントです。</p>
        <p><strong>最新iPhoneが欲しい人ほど、Appleで端末だけを買う前に、楽天モバイルへ乗り換えた場合の毎月の合計を確認する価値があります。</strong></p>
        <aside><strong>家族全員で端末を買う場合はカード名義を確認。</strong><p>2年返却の48回払いには、利用者本人名義の楽天カードが必要です。親の楽天カード1枚で家族全員分を契約することはできません。</p></aside>
      </section>

      <section id="sources"><h2>参照した公式情報</h2><ul><li><SourceLine href="https://network.mobile.rakuten.co.jp/fee/saikyo-plan/detail/">Rakuten最強プランの料金</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/service/rakuten-link/">Rakuten Linkの無料通話</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/campaign/mnp/">初回申込みのポイント特典</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/campaign/rakuten-employee/entry/vip/">三木谷キャンペーンの条件</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/product/iphone/iphone-18-pro/">iPhone 18 Proの価格</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/campaign/iphone-upgrade/">iPhone 18 Proのポイント特典</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/service/replacement-program/">買い替え超トクプログラム</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/faq/detail/10000208/">48回払いに必要な楽天カード</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/faq/detail/10000352/">iPhoneの購入可能台数</SourceLine></li><li><SourceLine href="https://network.mobile.rakuten.co.jp/faq/detail/10001139/">家族の契約名義とMNP</SourceLine></li></ul><p>キャンペーン確認日：2026年10月5日。料金・端末価格・キャンペーン・在庫は申込み直前に再確認してください。</p></section>

      <section id="apply">
        <h2>毎月の「ギガ・電話・端末代」の我慢を、一度に見直せる</h2>
        <p>少ない月は自動的に安く、たくさん使っても月3,278円。Rakuten Linkなら、長電話も料金を理由に切り上げなくて済みます。初めての回線申込みではポイントがもらえ、対象iPhoneを一緒に購入すれば端末特典も受けられます。</p>
        <p>料金と端末が自分に合ったら、最後に楽天モバイルのエリアマップで自宅や職場が対応エリアに入っているか確認します。</p>
        <p><strong>「今のスマホ代は仕方ない」と来月も同じ金額を払う前に、楽天モバイルなら家族の料金と欲しい端末がいくらになるか、一度確認してみてください。</strong></p>
        <div className="not-prose my-7 grid gap-4 md:grid-cols-2"><a href="https://network.mobile.rakuten.co.jp/product/iphone/" target="_blank" rel="noreferrer" className="border-y-2 border-foreground-900 bg-primary-600 px-5 py-5 text-center font-bold text-white">販売中のiPhoneを見る ↗</a><a href="https://network.mobile.rakuten.co.jp/campaign/" target="_blank" rel="noreferrer" className="border-y-2 border-foreground-900 bg-white px-5 py-5 text-center font-bold text-primary-700">最新キャンペーンを確認する ↗</a></div>
        <div className="not-prose my-7 border-y border-primary-300 bg-primary-50 px-5 py-5">
          <p className="text-xs font-bold text-primary-700">PR｜楽天アフィリエイトの広告リンク</p>
          <p className="mt-2 text-sm text-foreground-700">Rakuten最強プランの通常申込みを検討する方はこちら。上で紹介した三木谷キャンペーンなど、専用ページ限定の特典は適用されない場合があります。</p>
          <div className="mt-3 font-bold text-primary-700 underline [&_a]:text-primary-700" dangerouslySetInnerHTML={{ __html: approvedAds.rakuten }} />
        </div>
      </section>
    </>
  );
}

export default function CarrierEditorialArticle({ id }: { id: CarrierArticleId }) {
  if (id === "ahamo-guide") return <AhamoArticle />;
  if (id === "iijmio-guide") return <IijmioArticle />;
  return <RakutenArticle />;
}

